import { auth } from './auth.js';

const BASE = import.meta.env.VITE_API_URL;

async function req(path, { method = 'GET', body, isPublic = false } = {}) {
  const headers = { 'content-type': 'application/json' };
  if (!isPublic) {
    const token = await auth.getAccessToken();
    if (!token) throw new Error('Not authenticated');
    headers.authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const error = new Error(err.error ?? `HTTP ${res.status}`);
    error.status = res.status;
    error.detail = err;
    throw error;
  }
  return res.status === 204 ? null : res.json();
}

export const api = {
  // Profile
  getMe: () => req('/api/me'),
  updateMe: (body) => req('/api/me', { method: 'PUT', body }),
  deleteAccount: () =>
    req('/api/me', {
      method: 'DELETE',
      body: { confirm: 'DELETE' },
    }),
  updateNotifications: (prefs) => req('/api/me/notifications', { method: 'PUT', body: prefs }),

  // Items
  listItems: (cursor) => req(`/api/items${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
  getItem: (id) => req(`/api/items/${id}`),
  createItem: (body) => req('/api/items', { method: 'POST', body }),
  updateItem: (id, body) => req(`/api/items/${id}`, { method: 'PATCH', body }),
  deleteItem: (id) => req(`/api/items/${id}`, { method: 'DELETE' }),
  // Item lifecycle
  recoverItem: (itemId, info = {}) =>
    req(`/api/items/${itemId}/recover`, { method: 'POST', body: info }),
  markItemLost: (itemId) =>
    req(`/api/items/${itemId}/lost`, { method: 'POST', body: {} }),
  reactivateItem: (itemId) =>
    req(`/api/items/${itemId}/reactivate`, { method: 'POST', body: {} }),
  createScaffoldItem: (name, bountyCents, reportId) =>
    req('/api/items/scaffold', {
      method: 'POST',
      body: { name, bountyCents, reportId },
    }),

  // Categories / tags
  getCategories: () => req('/api/categories'),
  addCategory: (category, subcategory) =>
    req('/api/categories', { method: 'POST', body: { category, subcategory } }),
  addTag: (tag) =>
    req('/api/tags', { method: 'POST', body: { tag } }),

  // Claims
  listClaims: (itemId) => req(`/api/items/${itemId}/claims`),
  resolveClaim: (itemId, claimId, decision) =>
    req(`/api/items/${itemId}/claims/${claimId}/resolve`, { method: 'POST', body: { decision } }),

  // Found reports (per-person QR)
  listReports: () => req('/api/reports'),

  // Unified inbox
  listInbox: () => req('/api/inbox'),

  // Reports (upgraded)
  viewReport: (reportId) =>
    req(`/api/reports/${reportId}/view`, { method: 'POST', body: {} }),
  confirmReport: (reportId, matchedItemIds) =>
    req(`/api/reports/${reportId}/confirm`, { method: 'POST', body: { matchedItemIds } }),
  rejectReport: (reportId, reason) =>
    req(`/api/reports/${reportId}/reject`, { method: 'POST', body: { reason } }),

  // Photos
  presignItemUpload: (contentType) =>
    req('/api/items/upload-url', { method: 'POST', body: { contentType } }),
  signPhotoView: (key) => req(`/api/photos/sign?key=${encodeURIComponent(key)}`),

  // Public (no auth)
  publicGetItem: (id) => req(`/public/items/${id}`, { isPublic: true }),
  
  //delete cats/subs/tags
  deleteCategory: (category) =>
    req('/api/categories', { method: 'DELETE', body: { category } }),
  deleteSubcategory: (category, subcategory) =>
    req('/api/categories/sub', { method: 'DELETE', body: { category, subcategory } }),
  deleteTag: (tag) =>
    req('/api/tags', { method: 'DELETE', body: { tag } }),

  //Batch uodate
  batchUpdateItems: (itemIds, set) =>
    req('/api/items/batch', { method: 'POST', body: { itemIds, set } }),
};

/** Upload a File to S3 via the presigned URL flow. */
export async function uploadPhoto(file) {
  const { uploadUrl, key } = await api.presignItemUpload(file.type);
  const r = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': file.type },
    body: file,
  });
  if (!r.ok) throw new Error(`Upload failed (HTTP ${r.status})`);
  return key;
}
